import React from 'react';
// eslint-disable-next-line import/no-extraneous-dependencies
import { cx } from 'cvb';
import get from 'lodash/get';
import PropTypes from 'prop-types';
import dynamic from 'next/dynamic';
import constants from '../../config/constants';
import DiscountBadge from '../Badges/DiscountBadge/DiscountBadge';
import ProPrice from '../ProPrice/ProPrice';
import ProPricePrice from '../ProPrice/Price';
import { CesPricesStyles } from './CesPrices.style';
import _ from '../../utils/LodashImports';
import { withApplicationContext } from '../../utils/ApplicationContext';

const WholesaleBadge = dynamic(() => import('../WholesaleBadge/WholesaleBadge'), {
  ssr: false,
});

const CesPrices = ({
  getPrice,
  variant,
  regionCode,
  getUnitPrice,
  discountBadge,
  isCmrPrice,
  proPrice,
  cesPrice,
  appCtx,
  mode,
}) => {
  const isWholesaleEnabled = _.get(appCtx, 'siteConfig.toggles.isWholesaleEnabled', false);
  const cmrPrice = get(variant, 'prices', []).find((price) => price.type === constants.PRICE_TYPE.cmr);

  const highestPrice = get(variant, 'prices', [])
    .filter((price) => price.type !== constants.PRICE_TYPE.cmr)
    .reduce(
      (max, price) =>
        !max || parseFloat(get(price, 'price[0]', 0)) > parseFloat(get(max, 'price[0]', 0)) ? price : max,
      null
    );

  const { campaignName, badge, metadata, prices } = cesPrice;

  const cesBadgeLabel = constants.CES_BADGE_LABEL[campaignName];
  const isCesAndNormalPrice = !!cesPrice && !!highestPrice && (!cmrPrice || !proPrice);
  const isCesAndProPrice = cesPrice && proPrice && !cmrPrice;

  const renderUnitPrice = (unitPrice, className, symbol, price, unit, isNormalPrice = false) => {
    return (
      <div>
        {Object.entries(unitPrice).length ? (
          <p className={`ces-unit-price-${className}`}>
            {isNormalPrice && 'Normal '}
            {getUnitPrice(unitPrice, symbol)}
            <span className={`ces-unit-${className}`}>
              {' '}
              | {getPrice(price, symbol)} {unit?.toLowerCase()}
            </span>
          </p>
        ) : (
          <p className={cx(`ces-unit-price-${className}`, mode)}>
            {isNormalPrice && 'Normal '}
            {`${getPrice(price, symbol)} und`}
          </p>
        )}
        <style jsx>{CesPricesStyles}</style>
      </div>
    );
  };

  const renderCmrPrice = () => (
    <div className="cmr-price-container">
      <div className="discount-badge-cmr-container">
        <i className={cx('unica-cmr-icon', regionCode)} />
        {!!(discountBadge && Object.entries(discountBadge).length) && <DiscountBadge badge={discountBadge} />}
      </div>
      {renderUnitPrice(
        get(cmrPrice, 'unitPrice', {}),
        'cmr',
        get(cmrPrice, 'symbol', ''),
        get(cmrPrice, 'price', []),
        get(cmrPrice, 'unitPrice.unitForSale', '')
      )}
      <style jsx>{CesPricesStyles}</style>
    </div>
  );

  const renderCesPrice = () => {
    const cesClassName = cx('ces', isCesAndNormalPrice && 'big-text', isCesAndProPrice && 'ces-pro');

    return (
      <div>
        {badge && (
          <div className="ces-badges-container">
            <div className={`ces-badge ces-badge-${campaignName?.toLowerCase()}`}>{cesBadgeLabel}</div>
            {metadata?.discountPercent && (
              <div className={`ces-discount-badge ces-discount-badge-${campaignName?.toLowerCase()}`}>
                - {metadata?.discountPercent}%
              </div>
            )}
          </div>
        )}
        {renderUnitPrice(
          get(prices[0], 'unitPrice', {}),
          cesClassName,
          get(prices[0], 'symbol', ''),
          get(prices[0], 'price', []),
          get(variant, 'unitForSale', '')
        )}
        <style jsx>{CesPricesStyles}</style>
      </div>
    );
  };

  const renderHighestPrice = () => {
    const highestClassName = cx('highest', isCesAndNormalPrice && 'big-text', isCesAndProPrice && 'ces-pro');

    return (
      <div>
        {renderUnitPrice(
          get(highestPrice, 'unitPrice', {}),
          highestClassName,
          get(highestPrice, 'symbol', ''),
          get(highestPrice, 'price', []),
          get(highestPrice, 'unitPrice.unitForSale', ''),
          true
        )}
        <style jsx>{CesPricesStyles}</style>
      </div>
    );
  };

  return (
    <>
      {cmrPrice && renderCmrPrice()}
      {cesPrice && renderCesPrice()}
      {renderHighestPrice()}
      {proPrice && !isCmrPrice && (
        <>
          {isWholesaleEnabled && (
            <div className="wholesale-container">
              <WholesaleBadge
                quantityToBuyValue={proPrice?.metadata?.quantityToBuyValue}
                quantityToBuyOperator={proPrice.campaignName === constants.PRICE_TYPE.pro ? '>=' : '='}
                discountPercent={Number(proPrice?.metadata?.discountPercent)}
              />
              <ProPricePrice proPrice={proPrice} getPrice={getPrice} getUnitPrice={getUnitPrice} mode={mode} />
            </div>
          )}
          {!isWholesaleEnabled && (
            <ProPrice
              proPrice={proPrice}
              regionCode={regionCode}
              getPrice={getPrice}
              getUnitPrice={getUnitPrice}
              mode={mode}
            />
          )}
        </>
      )}
      <style jsx>{CesPricesStyles}</style>
    </>
  );
};

CesPrices.defaultProps = {
  getPrice: () => {},
  variant: {},
  regionCode: '',
  getUnitPrice: () => {},
  discountBadge: {},
  isCmrPrice: false,
  proPrice: {},
  cesPrice: {},
  appCtx: {},
  mode: 'PDP_MAIN',
};

CesPrices.propTypes = {
  getPrice: PropTypes.func,
  variant: PropTypes.object,
  regionCode: PropTypes.string,
  getUnitPrice: PropTypes.func,
  discountBadge: PropTypes.object,
  isCmrPrice: PropTypes.bool,
  proPrice: PropTypes.object,
  cesPrice: PropTypes.object,
  appCtx: PropTypes.object,
  mode: PropTypes.string,
};

export default withApplicationContext(CesPrices);
